import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import type {ApexOptions} from 'apexcharts'
import {HappyStateCard} from '@/components/worklist-row'
import {HelpIcon} from '@/components/help-icon'
import {ApexChart, useChartTokens} from '@/components/ui/apex-chart'
import {useIndicadores, useIndicadoresSerie, type Indicador} from '@/hooks/sgsst/incidentes/use-investigacion'
import {useIncidentesAbiertos} from '@/hooks/sgsst/incidentes/use-incidentes'
import {cn} from '@/lib/utils'

/** Same semantics as `TendenciaDelta` in `incidentes.tsx`; keep both in sync. */
function TendenciaChip({indicador}: {indicador: Indicador}) {
  const {tendencia, delta} = indicador
  const tone =
    tendencia === 'empeora' ? 'text-tone-red-fg' : tendencia === 'mejora' ? 'text-primary' : 'text-muted-foreground'
  const flecha = tendencia === 'empeora' ? '↑' : tendencia === 'mejora' ? '↓' : '→'
  const magnitud = delta === null || delta === undefined ? null : Math.abs(delta)
  return (
    <span
      data-testid={`salud-tendencia-${indicador.codigo}`}
      data-tendencia={tendencia}
      className={cn('inline-flex items-center gap-1 text-xs font-medium', tone)}>
      <span aria-hidden>{flecha}</span>
      {magnitud !== null && <span>{magnitud}</span>}
    </span>
  )
}

function MiniTrend({
  puntos,
  tendencia,
  label,
}: {
  puntos: {period_label: string; valor: number | null}[]
  tendencia: string
  label: string
}) {
  const tokens = useChartTokens()
  const stroke = tendencia === 'empeora' ? tokens['tone-red-fg'] : tokens['chart-hacer']
  const options = useMemo<ApexOptions>(
    () => ({
      chart: {sparkline: {enabled: true}},
      colors: [stroke],
      stroke: {width: 1.6, curve: 'smooth'},
      fill: {
        type: 'gradient',
        gradient: {shadeIntensity: 0, opacityFrom: 0.3, opacityTo: 0, stops: [0, 100]},
      },
      xaxis: {categories: puntos.map((p) => p.period_label), crosshairs: {show: false}},
      tooltip: {y: {title: {formatter: () => label}}},
    }),
    [stroke, label, puntos]
  )
  const series = useMemo(() => [{name: label, data: puntos.map((p) => p.valor ?? 0)}], [label, puntos])
  if (puntos.length < 2) return null
  return (
    <ApexChart type="area" height={48} className="mt-2" options={options} series={series} testId="salud-mini-trend" />
  )
}

function Tile({
  eyebrow,
  indicador,
  puntos,
  help,
  testId,
}: {
  eyebrow: string
  indicador: Indicador
  puntos?: {period_label: string; valor: number | null}[]
  help?: string
  testId: string
}) {
  const {t} = useTranslation()
  return (
    <div className="surface p-[18px]" data-testid={testId}>
      <p className="flex items-center gap-1.5 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
        {eyebrow}
        {help && (
          <HelpIcon label={t('common.help.iconLabel')} testId={`${testId}-help`}>
            {help}
          </HelpIcon>
        )}
      </p>
      <div className="mt-2 flex flex-wrap items-baseline gap-2">
        <span className="font-heading text-[28px] leading-none text-primary">
          {indicador.valor === null ? '—' : indicador.valor}
        </span>
        <TendenciaChip indicador={indicador} />
      </div>
      {puntos && <MiniTrend puntos={puntos} tendencia={indicador.tendencia} label={eyebrow} />}
    </div>
  )
}

function IncidentesAbiertosTile({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const {data: abiertos} = useIncidentesAbiertos(businessId)
  return (
    <Link
      to={`/businesses/${businessId}/incidentes`}
      className="surface block p-[18px] transition-colors hover:bg-muted"
      data-testid="salud-incidentes-abiertos">
      <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
        {t('dashboard.salud.incidentesAbiertos')}
      </p>
      <div className="mt-2 flex items-baseline gap-2">
        <span
          className={cn(
            'font-heading text-[28px] leading-none',
            abiertos && abiertos > 0 ? 'text-tone-red-fg' : 'text-primary'
          )}>
          {abiertos ?? '—'}
        </span>
      </div>
    </Link>
  )
}

export function SaludAccidentalidadSection({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const {data: indicadores} = useIndicadores(businessId, 'mes')
  const {data: serie} = useIndicadoresSerie(businessId, 12)

  if (!indicadores) return null

  const byCodigo = new Map(indicadores.indicadores.map((i) => [i.codigo, i]))
  const serieByCodigo = new Map((serie?.series ?? []).map((s) => [s.codigo, s.puntos]))
  const frecuencia = byCodigo.get('frecuencia')
  const severidad = byCodigo.get('severidad')
  const ausentismo = byCodigo.get('ausentismo')

  const sinEventos =
    (!frecuencia || frecuencia.numerador === 0) && (!severidad || severidad.numerador === 0) && !ausentismo?.numerador

  return (
    <div data-testid="salud-accidentalidad-section">
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <p className="font-mono text-[11px] font-medium tracking-wider text-muted-foreground uppercase">
          {t('dashboard.salud.eyebrow')}
        </p>
        <Link
          to={`/businesses/${businessId}/sgsst/indicadores`}
          className="text-xs font-medium text-primary hover:underline"
          data-testid="salud-ver-indicadores">
          {t('dashboard.salud.verIndicadores')}
        </Link>
      </div>
      {sinEventos ? (
        <HappyStateCard
          title={t('dashboard.salud.empty.title')}
          body={t('dashboard.salud.empty.body')}
          testId="salud-accidentalidad-empty"
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-4">
          {frecuencia && (
            <Tile
              eyebrow={t('dashboard.salud.frecuencia')}
              indicador={frecuencia}
              puntos={serieByCodigo.get('frecuencia')}
              testId="salud-frecuencia"
            />
          )}
          {severidad && (
            <Tile
              eyebrow={t('dashboard.salud.severidad')}
              indicador={severidad}
              puntos={serieByCodigo.get('severidad')}
              testId="salud-severidad"
            />
          )}
          {ausentismo && (
            <Tile
              eyebrow={t('dashboard.salud.ausentismo')}
              indicador={ausentismo}
              puntos={serieByCodigo.get('ausentismo')}
              help={t('dashboard.salud.ausentismoHelp')}
              testId="salud-ausentismo"
            />
          )}
          <IncidentesAbiertosTile businessId={businessId} />
        </div>
      )}
    </div>
  )
}
